'use client';
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-param-reassign */
import React, { useEffect } from 'react';
import DayPicker, { DateUtils } from 'react-day-picker';

import { DateTime } from 'luxon';
import { IExtendedDatePickerProps, DateRating } from '../interfaces';

import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { Icon } from '@benzinga/core-ui';

const getDateRange = (range: DateRating) => {
  let start: null | Date = null;
  let end: null | Date = null;

  switch (range) {
    case 'this-week':
      start = DateTime.now().startOf('week').toJSDate();
      end = DateTime.now().endOf('week').toJSDate();
      break;

    case 'this-month':
      start = DateTime.now().startOf('month').toJSDate();
      end = DateTime.now().endOf('month').toJSDate();
      break;

    case 'last-month': {
      const lastMonth = DateTime.now().minus({ months: 1 });
      start = lastMonth.startOf('month').toJSDate();
      end = lastMonth.endOf('month').toJSDate();
      break;
    }

    case 'rolling-7-days':
      start = DateTime.now().minus({ days: 7 }).toJSDate();
      end = new Date();
      break;

    case 'rolling-30-days':
      start = DateTime.now().minus({ days: 30 }).toJSDate();
      end = new Date();
      break;

    case 'week-to-date':
      start = DateTime.now().startOf('week').toJSDate();
      end = new Date();
      break;

    case 'today':
    case 'clear':
    default:
      start = new Date();
      end = new Date();
      break;
  }

  return [start, end];
};

let mounted = false;

const parseDate = (date: any, dateFormat: string) => {
  if (date) {
    return DateTime.fromFormat(date, dateFormat).toJSDate();
  }
  return null;
};

const ExtendedDatePicker = ({
  dateFormat = 'yyyy-MM-dd',
  end,
  onChangeDate,
  range = null,
  start,
}: IExtendedDatePickerProps) => {
  if (range) {
    [start, end] = getDateRange(range);
  }

  const [isVisible, setIsVisible] = React.useState(false);
  const [startDate, setStartDate] = React.useState<Date | null>(() => {
    const result = parseDate(start, dateFormat);
    return result && !isNaN(result.getTime()) ? result : null;
  });
  const [endDate, setEndDate] = React.useState<Date | null>(() => {
    const result = parseDate(end, dateFormat);
    return result && !isNaN(result.getTime()) ? result : null;
  });
  const [enteredTo, setEnteredTo] = React.useState<Date | undefined>(() => {
    const result = parseDate(end, dateFormat);
    return result && !isNaN(result.getTime()) ? result : undefined;
  });

  useEffect(() => {
    const startResult = parseDate(start, dateFormat);
    const endResult = parseDate(end, dateFormat);
    const enteredToResult = parseDate(end, dateFormat);

    if (start == null) {
      setStartDate(null);
    } else if (startResult && !isNaN(startResult.getTime())) {
      setStartDate(startResult);
    }

    if (end == null) {
      setEndDate(null);
      setEnteredTo(undefined);
    } else if (endResult && !isNaN(endResult.getTime())) {
      setEndDate(endResult);
      if (enteredToResult && !isNaN(enteredToResult.getTime())) {
        setEnteredTo(enteredToResult);
      }
    }
  }, [start, end, dateFormat]);

  const hidePicker = () => {
    setIsVisible(false);
  };

  useEffect(() => {
    if (!mounted) {
      window.addEventListener('click', () => {
        handleClickOutside();
      });
    }

    mounted = true;
  });

  const togglePicker = () => {
    setIsVisible(!isVisible);
  };

  const handleClickOutside = () => {
    hidePicker();
  };

  const formatDate = (date: Date | null) => {
    if (date) {
      return DateTime.fromJSDate(date).toFormat(dateFormat);
    }

    return null;
  };

  const formatViewDate = (date: Date | null) => {
    if (date && !isNaN(date.getTime())) {
      return DateTime.fromJSDate(date).toFormat('MM/dd/yyyy');
    }

    return null;
  };

  const resetDate = () => {
    setStartDate(null);
    setEndDate(null);
  };

  const handleDayClick = (day: any) => {
    if (startDate && endDate && day >= startDate && day <= endDate) {
      resetDate();
      return;
    }
    if (isSelectingFirstDay(startDate, endDate, day)) {
      setStartDate(day);
      setEndDate(null);
      setEnteredTo(undefined);
    } else {
      setEndDate(day);
      setEnteredTo(day);

      onChangeDate({
        end: formatDate(day),
        interval: true,
        start: formatDate(startDate),
      });

      // window.ga('send', 'event', 'User Interaction', 'Selected date range', `${formatViewDate(startDate)} - ${formatViewDate(day)}`);
    }
  };

  const isSelectingFirstDay = (from: Date | null, to: Date | null, day: Date) => {
    const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
    const isRangeSelected = from && to;
    return !from || isBeforeFirstDay || isRangeSelected;
  };

  const handleDayMouseEnter = (day: Date) => {
    if (!isSelectingFirstDay(startDate, endDate, day)) {
      setEnteredTo(day);
    }
  };

  const handleDatePreset = (range: any, label: string) => {
    const [start, end] = getDateRange(range);

    if (start !== null && end !== null && onChangeDate) {
      onChangeDate({
        end: formatDate(end),
        interval: true,
        start: formatDate(start),
      });

      setStartDate(start);
      setEndDate(end);
      setEnteredTo(end);
    }

    if (typeof window !== 'undefined')
      (window as any).ga('send', 'event', 'User Interaction', 'Selected preset date range', label);
  };

  const presets = [
    { id: 'today', label: 'Today' },
    { id: 'week-to-date', label: 'WTD' },
    { id: 'rolling-7-days', label: 'Trailing 7-day' },
    { id: 'rolling-30-days', label: 'Trailing 30-day' },
    // { id: 'this-week', label: 'This Week' },
    // { id: 'this-month', label: 'This Month' },
    // { id: 'last-month', label: 'Last Month' },
    // { id: 'clear', label: 'Clear' },
  ];

  const datePicker = (
    <DayPicker
      modifiers={{ end: enteredTo, start: startDate ?? undefined }}
      numberOfMonths={2}
      onDayClick={handleDayClick}
      onDayMouseEnter={handleDayMouseEnter}
      selectedDays={[startDate ?? undefined, { from: startDate ?? undefined, to: enteredTo ?? undefined }]}
    />
  );

  const presetButtons = (
    <div className="range-date-picker__presets">
      {presets.map(({ id, label }) => (
        <button
          className="range-date-picker__preset-btn"
          key={id}
          onClick={() => handleDatePreset(id, label)}
          type="button"
        >
          {label}
        </button>
      ))}
    </div>
  );

  const Input = ({ onClick }: any) => {
    let dateString: string | null = '';
    if (startDate && endDate) {
      dateString = `${formatViewDate(startDate)} - ${formatViewDate(endDate)}`;
    } else if (startDate) {
      dateString = formatViewDate(startDate);
    } else {
      dateString = 'Select Date';
    }

    return (
      <>
        <input
          className="range-date-picker__picker-field"
          onChange={value => console.log(value)}
          onClick={onClick}
          placeholder="Select Date"
          value={dateString || ''}
        />
        <Icon className="range-date-picker__icon" icon={faCalendar} />
      </>
    );
  };

  return (
    <div
      className="range-date-picker"
      onClick={event => event.stopPropagation()}
      // type="button"
    >
      {/* <span className="range-date-picker__label">Date:</span> */}

      <div className="range-date-picker__field-wrapper">
        <Input onClick={togglePicker} />
      </div>

      {isVisible && (
        <div className="range-date-picker__calendar-wrapper">
          {datePicker}
          {presetButtons}
        </div>
      )}
    </div>
  );
};

export default ExtendedDatePicker;
